<script setup lang="ts">
import type { IconName } from '@ui/components/icon/types'
import type { SizesType } from '@ui/styles'
import Icon from '@ui/components/icon/Icon.vue'
import { Sizes, TagType } from '@ui/styles'
import AngleDown from '~icons/cue-regular/angle-down'
import AngleDownSmall from '~icons/cue-regular/angle-down-small'
import Times from '~icons/cue-regular/times'

withDefaults(
  defineProps<{
    variant?: TagType
    size?: SizesType
    label?: string
    open?: boolean
    dot?: boolean
    icon?: IconName
    disabled?: boolean
    onClose?: () => void
    onOpen?: () => void
  }>(),
  {
    variant: TagType.Base,
    size: Sizes.R2D2,
    label: '',
    open: false,
    disabled: false,
  },
)
</script>

<template lang="pug">
.tag.flex-center-center(:class="[size, variant, disabled && 'disabled', $attrs.onClick && 'clickable']")
  Icon.icon-left(v-if="icon" :name="icon")
  .flex-center-center.row-4.p-0-4
    .dot.circle.s-8.m-r-6(v-if="dot")
    span
      slot
        | {{ $t(label) }}
    Icon.icon-chevron.cursor-pointer(v-if="onOpen" :class="{ open }" :name="size === Sizes.HanSolo ? AngleDownSmall : AngleDown" @click.stop="onOpen")
  Icon.icon-close.s-16.cursor-pointer(v-if="onClose" :name="Times" @click.stop="onClose")
</template>

<style scoped>
.tag {
  --uno: br-9999;
  width: max-content;
  transition: background-color 0.15s ease;

  &.clickable {
    cursor: pointer;
  }

  .icon-left,
  .icon-chevron,
  .icon-close {
    color: var(--cue-content-neutral-primary);
  }

  &.r2d2 {
    --uno: h-24 typ-12-18-400 p-0-8;

    .icon-left,
    .icon-chevron {
      --uno: s-16;
    }
  }

  &.leia {
    --uno: h-24 typ-14-20-500 p-0-8;

    .icon-left,
    .icon-chevron {
      --uno: s-16;
    }
  }

  &.hanSolo {
    --uno: h-32 typ-14-20-500 p-4-8;

    .icon-left,
    .icon-chevron {
      --uno: s-20;
    }
  }

  /* ── Accent (yellow/mustard) ────────────────────────── */
  &.accent {
    background: var(--cue-bg-accent-soft);
    color: var(--cue-content-accent-on-soft);

    .dot {
      background: var(--cue-content-accent-default);
    }

    &.clickable:hover {
      background: var(--cue-bg-accent-soft-hover);
    }
    &.clickable:active {
      background: var(--cue-bg-accent-soft-pressed);
    }
  }

  /* ── Primary (aubergine/purple) ─────────────────────── */
  &.primary {
    background: var(--cue-bg-primary-soft-dark);
    color: var(--cue-content-primary-on-soft);

    .dot {
      background: var(--cue-content-primary-default);
    }

    &.clickable:hover {
      background: var(--cue-bg-primary-soft-dark-hover);
    }
    &.clickable:active {
      background: var(--cue-bg-primary-soft-dark-pressed);
    }
  }

  /* ── Success (green) ────────────────────────────────── */
  &.success {
    background: var(--cue-bg-success-soft);
    color: var(--cue-content-success-on-soft);

    .dot {
      background: var(--cue-content-success-default);
    }

    &.clickable:hover {
      background: var(--cue-bg-success-soft-hover);
    }
    &.clickable:active {
      background: var(--cue-bg-success-soft-pressed);
    }
  }

  /* ── Info (blue) ────────────────────────────────────── */
  &.info {
    background: var(--cue-bg-info-soft);
    color: var(--cue-content-info-on-soft);

    .dot {
      background: var(--cue-content-info-default);
    }

    &.clickable:hover {
      background: var(--cue-bg-info-soft-hover);
    }
    &.clickable:active {
      background: var(--cue-bg-info-soft-pressed);
    }
  }

  /* ── Error (coral) ──────────────────────────────────── */
  &.error {
    background: var(--cue-bg-error-soft);
    color: var(--cue-content-error-on-soft);

    .dot {
      background: var(--cue-content-error-default);
    }

    &.clickable:hover {
      background: var(--cue-bg-error-soft-hover);
    }
    &.clickable:active {
      background: var(--cue-bg-error-soft-pressed);
    }
  }

  /* ── Neutral (gray) ─────────────────────────────────── */
  &.neutral {
    background: var(--cue-bg-neutral-secondary);
    color: var(--cue-content-neutral-primary);

    .dot {
      background: var(--cue-content-neutral-tertiary);
    }

    &.clickable:hover {
      background: var(--cue-bg-neutral-secondary-hover);
    }
    &.clickable:active {
      background: var(--cue-bg-neutral-secondary-pressed);
    }
  }

  /* ── Base ───────────────────────────────────────────── */
  &.base {
    background: var(--cue-bg-neutral-primary);
    color: var(--cue-content-neutral-primary);

    .dot {
      background: var(--cue-content-neutral-tertiary);
    }

    &.clickable:hover {
      background: var(--cue-bg-neutral-primary-hover);
    }
    &.clickable:active {
      background: var(--cue-bg-neutral-primary-pressed);
    }
  }

  /* ── Base Border ────────────────────────────────────── */
  &.base-border {
    background: var(--cue-bg-neutral-primary);
    color: var(--cue-content-neutral-primary);
    border: 1px solid var(--cue-border-neutral-secondary);

    .dot {
      background: var(--cue-content-neutral-tertiary);
    }

    &.clickable:hover {
      background: var(--cue-bg-neutral-primary-hover);
    }
    &.clickable:active {
      background: var(--cue-bg-neutral-primary-pressed);
    }
  }

  /* ── Disabled (all variants) ────────────────────────── */
  &.disabled {
    background: var(--cue-bg-neutral-disabled);
    color: var(--cue-content-neutral-disabled);
    cursor: not-allowed;

    .icon-left,
    .icon-close,
    .icon-chevron {
      color: var(--cue-content-neutral-disabled);
    }
  }

  /* ── Focus ──────────────────────────────────────────── */
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--cue-border-primary-soft);
  }
}

.icon-chevron {
  &.open {
    transform: rotate(180deg);
  }
}
</style>
